import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Select from "react-select";
import { getCities, getJobTitles } from "../../../service/employerapi";

const CandidateSearchBar = ({
  initialJobTitle = "",
  initialCity = "",
  initialSkills = "",
  initialEducation = "",
  recordLimit = 12,
  btnLoading = false,
  onSearch,
  customBasePath = "",
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const suggestionRef = useRef(null);

  // ---------------- STATES ----------------
  const [jobTitle, setJobTitle] = useState(initialJobTitle);
  const [city, setCity] = useState(initialCity);
  const [skills, setSkills] = useState(initialSkills);
  const [education, setEducation] = useState(initialEducation);

  const [cities, setCities] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);

  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // ---------------- LOAD JOB TITLE SUGGESTIONS ----------------
  const loadSuggestions = async (text) => {
    if (!text) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    try {
      const res = await getJobTitles(text);
      if (res.status === 200 && res.data?.data) {
        setSuggestions(res.data.data);
        setShowSuggestions(true);
      }
    } catch (e) {
      console.error("Suggestion error:", e);
    }
  };

  // ---------------- LOAD CITIES ----------------
  useEffect(() => {
    const fetchCities = async () => {
      try {
        setLoadingCities(true);
        const res = await getCities();
        if (res.data?.data) {
          setCities(
            res.data.data.map((c) => ({
              value: c,
              label: c,
            })),
          );
        }
      } catch (err) {
        console.error("City fetch error:", err);
      } finally {
        setLoadingCities(false);
      }
    };
    fetchCities();
  }, []);

  // ---------------- SYNC WITH URL ----------------
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setJobTitle(params.get("jobProfile") || "");
    setCity(params.get("city") || "");
    setSkills(params.get("skills") || "");
    setEducation(params.get("education") || "");
  }, [location.search]);

  // ---------------- CLICK OUTSIDE (SUGGESTIONS) ----------------
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (suggestionRef.current && !suggestionRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ---------------- SEARCH ----------------
  const handleSearch = (e) => {
    e.preventDefault();

    const params = new URLSearchParams();
    if (jobTitle) params.append("jobProfile", jobTitle);
    if (city) params.append("city", city);
    if (skills) params.append("skills", skills);
    if (education) params.append("education", education);
    params.append("page", 1);
    params.append("record_limit", recordLimit);

    const basePath = customBasePath || location.pathname;
    navigate(`${basePath}?${params.toString()}`);

    if (onSearch) {
      onSearch({
        jobTitle,
        city,
        skills,
        education,
      });
    }
  };

  // ---------------- RESET ----------------
  const handleReset = () => {
    setJobTitle("");
    setCity("");
    setSkills("");
    setEducation("");
    setShowSuggestions(false);

    navigate(location.pathname);

    if (onSearch) {
      onSearch({
        jobTitle: "",
        city: "",
        skills: "",
        education: "",
      });
    }
  };

  return (
    <form onSubmit={handleSearch}>
      <div className="side-job-search">
        <div className="searchBar innersearch">
          <div className="row-group-search">
            {/* -------- JOB TITLE -------- */}
            <div className="form-group form-group-1 hj-form">
              <div style={{ position: "relative" }} ref={suggestionRef}>
                <i className="fa-solid fa-magnifying-glass"></i>

                <input
                  type="text"
                  className="input-filter-search"
                  placeholder="Job title, keywords..."
                  value={jobTitle}
                  onChange={(e) => {
                    setJobTitle(e.target.value);
                    loadSuggestions(e.target.value);
                  }}
                />

                {showSuggestions && suggestions.length > 0 && (
                  <div
                    className="suggestion-box"
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      right: 0,
                      background: "#fff",
                      border: "1px solid #ddd",
                      zIndex: 1000,
                      borderRadius: "6px",
                      maxHeight: "200px",
                      overflowY: "auto",
                    }}
                  >
                    {suggestions.map((item, index) => (
                      <div
                        key={index}
                        style={{
                          padding: "8px 10px",
                          cursor: "pointer",
                        }}
                        className="suggestion-item"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          setJobTitle(item);
                          setShowSuggestions(false);
                        }}
                      >
                        {item}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* -------- CITY -------- */}
            <div className="form-group form-group-2">
              <i className="fa-solid fa-location-crosshairs"></i>
              <Select
                options={cities}
                isLoading={loadingCities}
                placeholder="Search Location..."
                value={cities.find((c) => c.value === city) || null}
                onChange={(opt) => setCity(opt ? opt.value : "")}
                classNamePrefix="react-select"
              />
            </div>

            {/* -------- SKILLS -------- */}
            <div className="form-group form-group-1">
              <i className="fa-solid fa-code"></i>
              <input
                type="text"
                className="input-filter-search"
                placeholder="Skills (React, PHP...)"
                value={skills}
                onChange={(e) => setSkills(e.target.value)}
              />
            </div>

            {/* -------- EDUCATION -------- */}
            <div className="form-group form-group-1">
              <i className="fa-solid fa-graduation-cap"></i>
              <input
                type="text"
                className="input-filter-search"
                placeholder="Education (B.Tech, MBA...)"
                value={education}
                onChange={(e) => setEducation(e.target.value)}
              />
            </div>

            {/* -------- BUTTONS -------- */}
            <div className="form-group-4 d-flex align-items-center">
              <button type="submit" className="searchbtn" disabled={btnLoading}>
                {btnLoading ? "Searching..." : "Search"}
              </button>

              <button
                type="button"
                className="searchbtn"
                style={{
                  backgroundColor: "#270b79",
                  border: "1px solid #270b79",
                  marginLeft: "4px",
                }}
                onClick={handleReset}
              >
                Reset
              </button>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
};

export default CandidateSearchBar;
